import * as React from "react";
import { Button, Input, Popup } from "semantic-ui-react";
import { PolymorphicPlayerCard, PlayerCardServices } from "@/services/bedrock";
import { useFormContext } from "react-hook-form";
import PlayerCardField from "./Fields/PlayerCardField";
import PlayerCardImageField from "./Fields/PlayerCardImageField";
import { LIQUID_PLACEHOLDER_REGEX } from "@/utils/liquidHelpers";

interface LinkWidgetSettingsProps {
  index: number;
}

const LinkWidgetSettings: React.FC<LinkWidgetSettingsProps> = ({ index }) => {
  const { setValue, getValues } = useFormContext<PolymorphicPlayerCard>();
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>();

  const [isFetching, setIsFetching] = React.useState(false);

  const fetchPageMetadata = () => {
    const linkUrl = getValues(`widgets.${index}.owner.url`);
    try {
      const url = new URL(linkUrl);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        alert("Only http and https links are supported");
        return;
      }
    } catch (err) {
      alert("Please enter a valid URL");
      return;
    }

    setIsFetching(true);
    PlayerCardServices.fetchUrlMetadata(linkUrl)
      .then(async metadata => {
        setValue(`widgets.${index}.owner.title`, metadata.title);

        const { image, logo } = metadata;
        const images = [image, logo].filter(Boolean);
        let thumbnailUrl: string | null = null;
        if (images.length === 1) {
          thumbnailUrl = images[0]?.url;
        } else if (images.length === 2) {
          let imageDiffFromSquare = Math.abs(1 - image.width / image.height);
          let logoDiffFromSquare = Math.abs(1 - logo.width / logo.height);
          if (imageDiffFromSquare < logoDiffFromSquare) {
            thumbnailUrl = image.url;
          } else if (imageDiffFromSquare > logoDiffFromSquare) {
            thumbnailUrl = logo.url;
          } else if (image.width >= logo.width) {
            thumbnailUrl = image.url;
          } else {
            thumbnailUrl = logo.url;
          }
        }

        setThumbnailUrl(thumbnailUrl);
        setTimeout(() => setThumbnailUrl(undefined), 1000);
      })
      .finally(() => setIsFetching(false));
  };

  return (
    <div className={`playerCardWidgetOwner playerCardLink ${isFetching ? "widgetLoading" : ""}`}>
      <PlayerCardField
        index={index}
        fieldName="url"
        rules={{
          required: "This is required.",
          validate: (value: string) => {
            try {
              const uri = new URL(value);
              if (uri.protocol !== "http:" && uri.protocol !== "https:") {
                return "Only http/https links are supported";
              }
              return true;
            } catch {
              const startsWithLiquidRegex = new RegExp(`^(${LIQUID_PLACEHOLDER_REGEX.source})`);
              if (startsWithLiquidRegex.test(value)) {
                try {
                  let testUrl = value.replace(startsWithLiquidRegex, "https://dummy.com");
                  testUrl = testUrl.replace(new RegExp(LIQUID_PLACEHOLDER_REGEX.source, "g"), "dummy");
                  const uri = new URL(testUrl);
                  if (uri.protocol !== "http:" && uri.protocol !== "https:") {
                    return "Only http/https links are supported";
                  }
                  return true;
                } catch {
                  return "Please enter a valid URL";
                }
              }
              return "Please enter a valid URL";
            }
          },
        }}
        component={Input}
        fieldLabel="URL"
        placeholder="URL"
        action={
          <Popup
            content="Refresh thumbnail and title using the URL's metadata"
            trigger={
              <Button icon="refresh" className="refreshLinkButton" loading={isFetching} onClick={fetchPageMetadata} />
            }
          />
        }
      />
      <PlayerCardField
        index={index}
        fieldName="title"
        rules={{ required: "This is required." }}
        fieldLabel="Title"
        component={Input}
        placeholder="Title"
        disabled={isFetching}
      />
      <div className="mediaContainer">
        <PlayerCardImageField
          index={index}
          fieldName="thumbnailMedia"
          fieldLabel="Thumbnail"
          cropShape="round"
          aspect={1}
          disabled={isFetching}
          imageUrl={thumbnailUrl}
        />
      </div>
    </div>
  );
};
export default LinkWidgetSettings;
